import React, { useState } from 'react'
import TextareaAutosize from 'react-autosize-textarea'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { EditorCloseIcon } from '@mote/assets/icons'
import { mote_primary } from '@mote/config/defaults/colors'
import { isRTLLanguage } from '@mote/helpers/l10n'
import { Button } from '@mote/ui'
import { CompactCard } from '../Cards/CompactCard'
import { EditorSaveToMotebookDialog } from './EditorSaveToMotebookDialog'
import { TranslationPicker } from './TranslationPicker'

const textareaStyle = {
  width: 'calc(100% - 20px)',
  maxHeight: '180px',
  padding: '10px',
  background: '#F8F8F8',
  overflowY: 'auto',
  resize: 'none',
  borderRadius: '10px',
  border: '1px solid #E9E9E9',
  outline: 'none'
}

export const Editor = ({
  audioUrl,
  transcription,
  setTranscription,
  dominantLanguage,
  onClose,
  onTranslate,
  onSave
}) => {
  const [showMotebook, setShowMotebook] = useState(false)

  const changeTranscription = e => {
    if (e.target && e.target.type === 'textarea') {
      setTranscription(e.target.value)
    }
  }

  const toggleMotebook = () => {
    setShowMotebook(state => !state)
  }

  const selectLanguage = langCode => {
    onTranslate(langCode)
  }

  const saveToMotebook = moteName => {
    toggleMotebook()
    onSave(moteName)
  }

  const maybeRTLEnhance = o => {
    const style = Object.assign({}, o)

    if (isRTLLanguage(dominantLanguage)) {
      style.direction = 'rtl'
    }

    return style
  }

  return createPortal(
    <Container>
      <Title>
        <span>Edit your Mote transcript</span>
        <div style={{ cursor: 'pointer' }} onClick={onClose}>
          <EditorCloseIcon />
        </div>
      </Title>
      <TextareaContainer>
        <TextareaAutosize
          onChange={changeTranscription}
          value={transcription}
          style={maybeRTLEnhance(textareaStyle)}
          autoFocus
        />
        <TranslationPicker onTranslate={selectLanguage} />
      </TextareaContainer>

      <CompactCard audioUrl={audioUrl} useInEditor />

      <Controls>
        <Button onClick={() => onSave('')} text='Confirm' />
        <SaveButton onClick={toggleMotebook} label='Save as'>
          Save to motebook
        </SaveButton>
      </Controls>

      {showMotebook && (
        <PositioningWrapper>
          <EditorSaveToMotebookDialog
            onClose={toggleMotebook}
            onSave={saveToMotebook}
          />
        </PositioningWrapper>
      )}
    </Container>,
    document.body
  )
}

const Container = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  padding: 1rem;
  background-color: #ffffff;
  align-self: center;
  width: 20rem;
  border-radius: 0.5rem;
  border-width: 1px;
  border-color: #d1d5db;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
`

const TextareaContainer = styled.div`
  position: relative;
`

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Title = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  justify-content: space-between;
  align-items: center;
`

const PositioningWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const SaveButton = styled.span`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  line-height: 1rem;
  cursor: pointer;
  color: ${mote_primary}
  
  &:hover {
    text-decoration: underline;
  }
`

Editor.propTypes = {
  audioUrl: PropTypes.string.isRequired,
  transcription: PropTypes.string,
  setTranscription: PropTypes.func.isRequired,
  dominantLanguage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onTranslate: PropTypes.func.isRequired
}
